import type { ViewFilters } from '../../lib/outreach/savedViews'
import type { CustomFieldDef } from '../../lib/outreach/types'

interface Props {
  filters: ViewFilters
  onChange: (next: ViewFilters) => void
  defs: readonly CustomFieldDef[]
  stages: readonly string[]
}

/** Ad-hoc contact filter used by the enrol dialog's "Custom filter" option. */
export function EnrollFilters({ filters, onChange, defs, stages }: Props) {
  const set = (patch: Partial<ViewFilters>) => onChange({ ...filters, ...patch })
  const cf = filters.customField

  return (
    <div className="ed-grid">
      <div className="form-group">
        <label className="form-label" htmlFor="ed-stage">
          Stage
        </label>
        <select
          id="ed-stage"
          className="form-select"
          value={filters.stage}
          onChange={(e) => set({ stage: e.target.value })}
        >
          <option value="">Any stage</option>
          {stages.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ed-tag">
          Tag
        </label>
        <input
          id="ed-tag"
          className="form-input"
          value={filters.tagFilter}
          onChange={(e) => set({ tagFilter: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ed-country">
          Country
        </label>
        <input
          id="ed-country"
          className="form-input"
          value={filters.country}
          onChange={(e) => set({ country: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ed-search">
          Text search
        </label>
        <input
          id="ed-search"
          className="form-input"
          value={filters.search}
          onChange={(e) => set({ search: e.target.value })}
        />
      </div>
      {defs.length > 0 && (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="ed-cf-key">
              Custom field
            </label>
            <select
              id="ed-cf-key"
              className="form-select"
              value={cf?.key ?? ''}
              onChange={(e) =>
                set({
                  customField: e.target.value
                    ? { key: e.target.value, value: cf?.value ?? '' }
                    : null,
                })
              }
            >
              <option value="">No custom field filter</option>
              {defs.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="ed-cf-value">
              Custom field value
            </label>
            <input
              id="ed-cf-value"
              className="form-input"
              disabled={!cf}
              value={cf?.value ?? ''}
              onChange={(e) => cf && set({ customField: { key: cf.key, value: e.target.value } })}
            />
          </div>
        </>
      )}
    </div>
  )
}

/** Styles for the enrol dialog. Everything uses tokens. */
export function EnrollStyles() {
  return (
    <style>{`
.ed-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:var(--space-3)}
.ed-stack{display:flex;flex-direction:column;gap:var(--space-4)}
.ed-chips{display:flex;gap:var(--space-2);flex-wrap:wrap}
.ed-chip{background:var(--bg3);border:1px solid var(--border);border-radius:999px;padding:2px var(--space-3);font-size:var(--text-sm);color:var(--text)}
.ed-note{font-size:var(--text-sm);color:var(--text-dim)}
.ed-warn{font-size:var(--text-sm);color:var(--amber)}
.ed-error{font-size:var(--text-sm);color:var(--red)}
.ed-check{display:flex;gap:var(--space-2);align-items:center;font-size:var(--text-base);color:var(--text)}
`}</style>
  )
}
